/**
 * Basement size buckets (Phase 8). Same pattern as
 * kitchen-size-ranges.ts: the brief asks for a bucketed size question
 * ("approximate square footage... use ranges initially") rather than a
 * bare numeric input, structured so it can be swapped for/supplemented
 * with an exact figure without any engine change - the engine only ever
 * sees a resolved `room_size` sqft value, never a bucket id.
 *
 * Bucket boundaries are wider than kitchen's (a basement is typically
 * 5-15x a kitchen's footprint) and centered on the existing
 * `DEFAULT_ROOM_SQFT.basement` planning default of 800 sqft (see
 * packages/estimator-app/src/lib/defaults.ts) so the middle bucket lines
 * up with what the UI already assumed before this phase.
 */
export interface BasementSizeRange {
  id: string;
  label: string;
  /** Representative square footage used to drive the calculation when this bucket is picked. */
  representativeSqft: number;
  min: number;
  /** null = no upper bound ("1,800+ sq. ft."). */
  max: number | null;
}

export const BASEMENT_SIZE_RANGES: BasementSizeRange[] = [
  { id: "under_500", label: "Under 500 sq. ft.", representativeSqft: 400, min: 0, max: 500 },
  { id: "500_800", label: "500–800 sq. ft.", representativeSqft: 650, min: 500, max: 800 },
  { id: "800_1200", label: "800–1,200 sq. ft.", representativeSqft: 1000, min: 800, max: 1200 },
  { id: "1200_1800", label: "1,200–1,800 sq. ft.", representativeSqft: 1500, min: 1200, max: 1800 },
  { id: "1800_plus", label: "1,800+ sq. ft.", representativeSqft: 2200, min: 1800, max: null }
];

export function findBasementSizeRange(id: string | undefined): BasementSizeRange | undefined {
  return BASEMENT_SIZE_RANGES.find((r) => r.id === id);
}

/** Finds the bucket a given exact square footage falls into, for keeping the bucket answer and an exact value in sync. */
export function basementSizeRangeForSqft(sqft: number): BasementSizeRange | undefined {
  return (
    BASEMENT_SIZE_RANGES.find((r) => sqft >= r.min && (r.max === null || sqft < r.max)) ??
    BASEMENT_SIZE_RANGES[BASEMENT_SIZE_RANGES.length - 1]
  );
}
